import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import { fetchBrandFavorites } from '@common/Favorites/actions/favoritesActions';
import { SEARCH_LOCAL_STORAGE_ID, SEARCH_OBJECT_TYPE } from '@common/GlobalSearch/constants';
import { getSearchParamsFromStorage } from '@common/GlobalSearch/helpers/getSearchParamsFromStorage';
import { useRecentSearch } from '@common/GlobalSearch/hooks/useRecentSearch';
import { useConfig } from '@common/Hooks/useConfig';
import { get } from '@common/Http';
import { ListingPage as ListingPageView } from '@common/Listing/components';
import ListingContextProvider from '@common/Listing/ListingContextProvider';
import { ReactOnRailsRootComponent } from '@common/ReactOnRails';
import { set } from '@helpers/cookie';
import { remove } from '@helpers/localStorage';
import { getFavoriteBrandsLastUpdated, getVisitorId } from '@helpers/visitor';
import { fetchProducts } from '../actions/listingActions';
import { getFiltersParameters } from '../helpers/filters';
import { ListingPageQueryListener } from './ListingPageRouter';

function ListingPage({
    baseNavigationId,
    descriptionBottom,
    brandDescriptionBottom,
    persistedFiltersGroup,
    filtersProps,
    listingTopHeadProps,
    linkParams,
    brandwall,
    isListingGrouped,
    breadcrumbs,
    faqs,
    faqTitleHeadingTag,
    faqQuestionHeadingTag,
    relatedListings
}) {
    const config = useConfig();
    const dispatch = useDispatch();
    const { addRecentSearch } = useRecentSearch();

    const query = useSelector(state => state.listing.query);
    const [hasProducts, setHasProducts] = useState(filtersProps.hasProducts);

    const RouterComponent = config.serverSide ? StaticRouter : BrowserRouter;
    const hasToShowDescriptions = parseInt(query.page, 10) === 1;
    const { isPremium, theme } = filtersProps;

    const handleProductsChange = (listingData) => {
        setHasProducts(!!listingData?.length);
    };

    useEffect(() => {
        if (listingTopHeadProps?.favorite) {
            dispatch(fetchBrandFavorites(getVisitorId(), getFavoriteBrandsLastUpdated()));
        }

        const searchQuery = new URLSearchParams(window.location.search);
        const hasVoucher = searchQuery.has('voucher');

        if (!hasVoucher) return;

        const setVouchers = async () => {
            const voucher = searchQuery.get('voucher');

            if (!voucher) return;

            const { expire } = await get(`/{store}/{language}/api/shopcart/voucher/validate/${voucher}`);

            if (expire) {
                const cookieContent = `${config.store}${encodeURIComponent(':')}${voucher}`;

                set('voucher', cookieContent, { expireDateString: expire, path: '/' });
            }
        };

        setVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const searchQuery = new URLSearchParams(window.location.search);
        const filtersParameters = getFiltersParameters(searchQuery);

        if (hasProducts || isEmpty(filtersParameters)) {
            return;
        }

        dispatch(fetchProducts(1));
    }, [hasProducts, dispatch]);

    useEffect(() => {
        const {
            action, searchKey, navigationId, path
        } = getSearchParamsFromStorage(SEARCH_LOCAL_STORAGE_ID.searchResults);

        if (!action) {
            return;
        }

        const searchType = brandwall ? SEARCH_OBJECT_TYPE.brandwall : SEARCH_OBJECT_TYPE.category;

        addRecentSearch({
            searchKey: searchKey || query.search_key,
            action,
            objectType: searchType,
            navigationId: navigationId || null,
            path: path || null,
            eanId: null,
            productId: null
        });

        remove(SEARCH_LOCAL_STORAGE_ID.searchSource);
        remove(SEARCH_LOCAL_STORAGE_ID.searchResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <RouterComponent location={config.location}>
            <ListingContextProvider
                hasProducts={hasProducts}
                isPremium={isPremium}
                theme={theme}
            >
                <ListingPageView
                    brandDescriptionBottom={brandDescriptionBottom}
                    breadcrumbs={breadcrumbs}
                    descriptionBottom={hasToShowDescriptions ? descriptionBottom : null}
                    faqQuestionHeadingTag={faqQuestionHeadingTag}
                    faqs={faqs}
                    faqTitleHeadingTag={faqTitleHeadingTag}
                    filtersProps={filtersProps}
                    isListingGrouped={isListingGrouped}
                    linkParams={linkParams}
                    listingTopHeadProps={listingTopHeadProps}
                    persistedFiltersGroup={persistedFiltersGroup}
                    relatedListings={relatedListings}
                />
            </ListingContextProvider>
            <ListingPageQueryListener
                baseNavigationId={baseNavigationId}
                onProductsChange={handleProductsChange}
            />
        </RouterComponent>
    );
}

export const ListingPageSSR = ReactOnRailsRootComponent(props => (
    <ListingPage {...props} />
), { withReduxStore: true });
